'use client';

import { memo, useState, type MouseEvent } from 'react';
import { ShoppingCart, Star, ShieldCheck } from 'lucide-react';
import type { ProductSummary } from '@/lib/api';
import { addToCart, ApiError, checkAuth } from '@/lib/api';
import { laravelPaths } from '@/lib/site';
import { ThemedExternalLink } from '@/components/ThemedExternalLink';
import { ListingThumbnail } from '@/components/ListingThumbnail';
import { formatListingDate, listingCreatedAt } from '@/lib/formatListingDate';

interface ProductCardProps {
  product: ProductSummary;
  priority?: boolean;
}

function productTitle(p: ProductSummary) {
  return p.name ?? p.title ?? 'Product';
}

function productCondition(p: ProductSummary) {
  return (p as { condition?: string }).condition ?? 'New';
}

export const ProductCard = memo(function ProductCard({ product, priority = false }: ProductCardProps) {
  const [cartPending, setCartPending] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const title = productTitle(product);
  const isEcommerce = (product as { type?: string }).type !== 'CLASSIFIED';
  const hasOfficialStore = Boolean((product as { hasStore?: boolean }).hasStore);
  const currency = (product as { currency?: string }).currency ?? 'LKR';
  const sellerNameCandidates = [
    (product as { sellerName?: string | null }).sellerName,
    (product as { seller_name?: string | null }).seller_name,
    (product as { store?: { name?: string | null } | null }).store?.name,
    (product as { seller?: { name?: string | null } | null }).seller?.name,
    (product as { user?: { name?: string | null } | null }).user?.name,
  ];
  const sellerName = sellerNameCandidates.find((name) => typeof name === 'string' && name.trim().length > 0)?.trim() ?? 'Seller';
  const listedLabel = formatListingDate(
    listingCreatedAt(product as { created_at?: string | null; createdAt?: string | null }),
  );
  const hasSponsored = (product as { isPromoted?: boolean }).isPromoted;
  const discountPercentage = (product as { discountPercentage?: number }).discountPercentage;
  const productHref = product.slug ? laravelPaths.product(product.slug) : '#';
  const stock = (product as { stock?: number | null }).stock;
  const hasVariants = Boolean((product as { hasVariants?: boolean }).hasVariants);
  const allowBackorder = Boolean(
    (product as { allow_backorder?: boolean; allowBackorder?: boolean }).allow_backorder
      ?? (product as { allowBackorder?: boolean }).allowBackorder,
  );
  // Listing payloads often omit stock; only disable when we know quantity is zero.
  const isOutOfStock = isEcommerce && !allowBackorder && !hasVariants && typeof stock === 'number' && stock <= 0;

  const showGuestCartToast = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('app-toast', {
        detail: { message: 'Please login or register to buy products' },
      }),
    );
  };

  const performAddToCart = async () => {
    if (!isEcommerce || isOutOfStock || cartPending) return;
    setCartPending(true);
    setJustAdded(true);
    window.dispatchEvent(new CustomEvent('cart-updated', { detail: { delta: 1 } }));
    try {
      await addToCart(product.id);
    } catch (err) {
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: { delta: -1 } }));
      setJustAdded(false);
      if (err instanceof ApiError && err.statusCode === 401) {
        (window as Window & { __BUYME_AUTH__?: boolean }).__BUYME_AUTH__ = false;
        showGuestCartToast();
      }
    } finally {
      setCartPending(false);
      window.setTimeout(() => setJustAdded(false), 900);
    }
  };

  const handleAddToCart = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isEcommerce || isOutOfStock || cartPending) return;
    // Products with variants must pick a variant on the detail page before adding to cart.
    if (hasVariants) {
      if (typeof window !== 'undefined' && productHref !== '#') {
        window.location.href = productHref;
      }
      return;
    }
    const authFlag = (window as Window & { __BUYME_AUTH__?: boolean }).__BUYME_AUTH__;
    if (authFlag === false) {
      showGuestCartToast();
      return;
    }
    if (authFlag === undefined) {
      const isLoggedIn = await checkAuth();
      (window as Window & { __BUYME_AUTH__?: boolean }).__BUYME_AUTH__ = isLoggedIn;
      if (!isLoggedIn) {
        showGuestCartToast();
        return;
      }
    }
    await performAddToCart();
  };

  return (
    <div className="group relative bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex flex-col h-full cursor-pointer ring-0">
      {hasSponsored && (
        <span className="absolute top-2 left-2 md:top-3 md:left-3 z-10 bg-brand-orange text-white text-[8px] md:text-[10px] font-bold px-1.5 py-0.5 md:px-2 md:py-1 rounded shadow-sm uppercase tracking-wide">
          Sponsored
        </span>
      )}
      {discountPercentage != null && (
        <span
          className={[
            'absolute left-2 md:left-3 z-10 bg-red-500 text-white text-[8px] md:text-[10px] font-bold px-1.5 py-0.5 md:px-2 md:py-1 rounded shadow-sm',
            // When both badges are present, stack them instead of overlapping.
            hasSponsored ? 'top-8 md:top-9' : 'top-2 md:top-3',
          ].join(' ')}
        >
          -{discountPercentage}%
        </span>
      )}

      <ThemedExternalLink
        href={productHref}
        className="no-tap-highlight block flex-1 flex flex-col focus:outline-none focus-visible:outline-none focus-visible:ring-0"
      >
        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-t-xl bg-neutral-100 dark:bg-neutral-700">
          {product.image ? (
            <ListingThumbnail
              src={product.image}
              alt={title}
              priority={priority}
              className="object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-neutral-400">No image</div>
          )}
          <div className="absolute bottom-1 right-1 md:bottom-2 md:right-2 bg-brand-dark/80 text-white text-[8px] md:text-[10px] font-bold px-1.5 py-0.5 md:px-2 md:py-1 rounded backdrop-blur-sm">
            {productCondition(product)}
          </div>
        </div>

        <div className="p-3 md:p-4 flex-1 flex flex-col">
          <div className="flex justify-between items-center mb-2 h-[1.25rem] md:h-[1.5rem]">
            <span
              className={`text-[8px] md:text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full truncate max-w-[70%] ${
                hasOfficialStore ? 'bg-blue-100 text-blue-900' : 'bg-violet-50 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200'
              }`}
            >
              {hasOfficialStore ? 'OFFICIAL STORE' : 'MARKETPLACE'}
            </span>
            {(product as { rating?: number }).rating != null && (
              <div className="flex items-center text-yellow-500 text-[10px] md:text-xs font-bold">
                <Star size={12} className="fill-current mr-0.5" />
                {(product as { rating?: number }).rating}
              </div>
            )}
          </div>

          <h3 className="text-brand-dark dark:text-neutral-100 font-medium text-xs md:text-base leading-snug mb-2 h-[1.25rem] md:h-[1.5rem] line-clamp-1 group-hover:text-brand-orange transition-colors">
            {title}
          </h3>

          <div className="mt-auto">
            <div className="mb-2 h-[1.75rem] md:h-[2rem] flex items-center">
              <div className="flex items-center gap-1 md:gap-2 min-w-0">
                <span className="text-sm md:text-xl font-bold text-brand-green tracking-tight leading-none whitespace-nowrap">
                  {currency} {Number(product.price).toLocaleString()}
                </span>
                {(product as { comparePrice?: number; compare_price?: number }).comparePrice != null && (
                  <span className="text-[10px] md:text-xs text-neutral-400 dark:text-neutral-500 line-through truncate leading-none whitespace-nowrap">
                    {currency} {(product as { comparePrice?: number }).comparePrice?.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            <div className="mb-2 h-[1rem] md:h-[1.25rem] flex items-center justify-between gap-2 min-w-0">
              <div className="flex items-center gap-1 text-[10px] md:text-xs text-neutral-600 dark:text-neutral-400 min-w-0">
                {hasOfficialStore && <ShieldCheck size={12} className="text-blue-600 shrink-0" />}
                <span className="truncate">{sellerName}</span>
              </div>
              {listedLabel && (
                <time
                  dateTime={listingCreatedAt(product as { created_at?: string | null; createdAt?: string | null }) ?? undefined}
                  className="shrink-0 text-[10px] md:text-xs text-neutral-400 dark:text-neutral-500 tabular-nums"
                >
                  {listedLabel}
                </time>
              )}
            </div>
          </div>
        </div>
      </ThemedExternalLink>

      {isEcommerce ? (
        <button
          type="button"
          onClick={handleAddToCart}
          disabled={cartPending || isOutOfStock}
          className={`mx-3 mb-3 md:mx-4 md:mb-4 py-2 md:py-2.5 rounded-lg text-xs md:text-sm font-bold flex items-center justify-center gap-1.5 md:gap-2 transition-all duration-200 shadow-sm ${
            isOutOfStock
              ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
              : 'bg-brand-orange text-white hover:bg-brand-orange-hover'
          }`}
        >
          {justAdded ? (
            'Added'
          ) : isOutOfStock ? (
            'Out of stock'
          ) : (
            <>
              <ShoppingCart size={14} className="md:w-4 md:h-4 shrink-0" /> Add to Cart
            </>
          )}
        </button>
      ) : (
        <ThemedExternalLink
          href={productHref}
          className="mx-3 mb-3 md:mx-4 md:mb-4 py-2 md:py-2.5 rounded-lg text-xs md:text-sm font-bold flex items-center justify-center gap-1.5 md:gap-2 transition-all duration-200 shadow-sm bg-brand-orange text-white hover:bg-brand-orange-hover"
        >
          View listing
        </ThemedExternalLink>
      )}
    </div>
  );
});
